import React, { LegacyRef, forwardRef } from "react";

import { styled } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorOutlinedIcon from "@mui/icons-material/DragIndicatorOutlined";
import { DropTargetMonitor, useDrop, useDrag } from "react-dnd";
import { enqueueSnackbar } from "notistack";

import ConditionComponent, { ConditionProps } from "./ConditionComponent";
import Condition, { enum_conditional } from "../../models/conditions/Condition";
import { ConditionDragItem, draggable_types } from "../../helpers/DragHelper";
import { useRowSelector } from "../../store/Hooks";

const GroupContainer = styled("div")(({ theme }) => ({
  backgroundColor: "rgba(250, 240, 210, 0.5)",
  outline: "1px solid #DCC896",
  borderRadius: 5,
  display: "flex",
  width: "calc(100% - 20px)",
  padding: "10px",
  position: "relative",
}));

const GroupContent = styled("div")(({ theme }) => ({
  width: "100%",
}));

const ChildrenContainer = styled("div")(({ theme }) => ({
  paddingLeft: 24,
  "& >div": {
    position: "relative",
    marginTop: 10,
    marginBottom: 10,
  },
  "& >::before": {
    content: '""',
    borderColor: "#ccc",
    borderStyle: "solid",
    boxSizing: "border-box",
    position: "absolute",
    width: 22,
    left: -22,
    top: -11,
    height: "calc(50% + 11px)",
    borderWidth: "0 0px 2px 2px",
    borderRadius: "0 0 0 4px",
  },
  "& >:not(:last-child):after": {
    content: '""',
    borderColor: "#ccc",
    borderStyle: "solid",
    boxSizing: "border-box",
    position: "absolute",
    width: 22,
    left: -22,
    top: "calc(50% - 3px);",
    height: "calc(50% + 13px)",
    borderWidth: "0 0px 0px 2px",
  },
}));

const GroupHeader = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  width: "100%",
}));

const ToggleButtons = styled("div")(({ theme }) => ({
  flexGrow: 1,
}));

const AddGroup = styled(ButtonGroup)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    marginTop: "10px",
  },
}));

const ConditionalToggle = styled(ToggleButton)(({ theme }) => ({
  "&.Mui-selected, &.Mui-selected:hover": {
    color: "white",
    backgroundColor: theme.palette.info.main,
  },
}));

const NotToggle = styled(ToggleButton)(({ theme }) => ({
  "&.Mui-selected, &.Mui-selected:hover": {
    color: "white",
    backgroundColor: theme.palette.secondary.main,
  },
}));

const DragHandle = styled(DragIndicatorOutlinedIcon)(({ theme }) => ({
  cursor: "grab",
  margin: "auto",
  position: "absolute",
  top: "calc(50% - 11px)",
}));

const ConditionGroup = forwardRef(
  (props: ConditionProps, ref: LegacyRef<HTMLDivElement> | undefined) => {
    const condition = useRowSelector(Condition, props.condition_id);
    if (typeof condition === "undefined") {
      throw new Error("Unable to display a condition that does not exist");
    }
    const parent = useRowSelector(Condition, condition.parent_condition_id  as number);
    const draggable = condition.parent_condition_id  !== null;
    const readonly = props.readonly;

    // The ADD RULE button can also be used to drag conditions into a group
    // I added this mostly because it's hard to drag a condition into an empty group

    // This handles adding the hovering condition to this condition's children
    // while also removing the hovering condition from its old parent
    const [{ isOverAddRule }, addRuleRef] = useDrop<
      ConditionDragItem,
      void,
      { isOverAddRule: boolean | undefined }
    >(
      () => ({
        accept: draggable_types.Condition,
        canDrop: () => true,
        hover: (item: ConditionDragItem) => {
          if (
            item.parentID !== null &&
            item.parentID !== condition.id &&
            !readonly
          ) {
            // Check if hovering condition is a root condition i.e. parent = null (it shouldn't be, but good to check)
            // Also make sure that hovering condition isn't already a parent of this condition
            const newIndex = 0;
            Condition.moveCondition(
              item.conditionID,
              condition.id,
              newIndex,
              item.parentID,
            );
            // Update item, otherwise this will keep firing
            item.parentID = condition.id;
            item.index = newIndex;
          }
        },
        collect: (monitor: DropTargetMonitor) => ({
          isOverAddRule: monitor.isOver({ shallow: true }),
        }),
      }),
      [condition],
    );

    const [{ isDragging }, drag, preview] = useDrag<
      ConditionDragItem,
      void,
      { isDragging: boolean | undefined }
    >(
      () => ({
        type: draggable_types.Condition,
        canDrag: () => draggable,
        item: new ConditionDragItem(
          condition.id,
          props.index,
          condition.parent_condition_id,
        ),
        collect: (monitor) => ({
          isDragging: monitor.isDragging(),
        }),
      }),
      [condition, parent],
    );

    const handleNotSelect = () => {
      if (!readonly) {
        condition.update("not", !condition.not);
      }
    };

    const handleConditionalSelect = (
      event: React.MouseEvent<HTMLElement>,
      valueArray: string | null,
    ) => {
      if (valueArray !== null && !readonly) {
        const value = valueArray[0];
        if (value in enum_conditional) {
          condition.update(
            "conditional",
            enum_conditional[value as keyof typeof enum_conditional],
          );
        }
      }
    };

    const children = condition.children.map(
      (childID: number, index: number) => (
        <ConditionComponent
          key={childID}
          condition_id={childID}
          index={index}
          readonly={readonly}
        />
      ),
    );

    return (
      <GroupContainer
        ref={ref}
        sx={{
          ...props.sx,
        }}
      >
        {draggable && (
          <span ref={drag}>
            <DragHandle />
          </span>
        )}
        <GroupContent
          sx={{ marginLeft: draggable ? "22px" : "0px" }}
          ref={preview}
        >
          <GroupHeader>
            <ToggleButtons>
              <ToggleButtonGroup
                sx={{ marginRight: "10px" }}
                onChange={handleNotSelect}
              >
                <NotToggle
                  color="secondary"
                  value="NOT"
                  selected={condition.not}
                  disabled={readonly}
                >
                  NOT
                </NotToggle>
              </ToggleButtonGroup>
              <ToggleButtonGroup onChange={handleConditionalSelect}>
                <ConditionalToggle
                  value={enum_conditional.and}
                  selected={condition.conditional === enum_conditional.and}
                  disabled={readonly}
                >
                  AND
                </ConditionalToggle>
                <ConditionalToggle
                  value={enum_conditional.or}
                  selected={condition.conditional === enum_conditional.or}
                  disabled={readonly}
                >
                  OR
                </ConditionalToggle>
              </ToggleButtonGroup>
            </ToggleButtons>
            <AddGroup>
              <Button
                startIcon={<AddIcon />}
                onClick={() => {
                  if (!readonly) {
                    condition.addChild(
                      Condition.createInstance({
                        set_condition_id: condition.set_condition_id,
                        parent_condition_id : condition.id,
                        conditional: enum_conditional.anchor_property,
                      }) as Condition,
                    );
                  }
                }}
                disabled={readonly}
                ref={addRuleRef}
                sx={isOverAddRule ? { cursor: "copy" } : {}}
              >
                Add rule
              </Button>
              <Button
                startIcon={<AddIcon />}
                onClick={() => {
                  if (!readonly) {
                    condition.addChild(
                      Condition.createInstance({
                        set_condition_id : condition.set_condition_id,
                        parent_condition_id : condition.id,
                        conditional: enum_conditional.and,
                      }) as Condition,
                    );
                  }
                }}
                disabled={readonly}
              >
                Add group
              </Button>
              {condition.set_condition_id !== condition.id && (
                <Button
                  onClick={() => {
                    if (!readonly) {
                      if (typeof parent !== "undefined") {
                        parent?.removeChild(condition as Condition);
                      } else {
                        enqueueSnackbar(
                          "If you want to delete the top level group, please use the delete button below",
                          { variant: "error" },
                        );
                      }
                    }
                  }}
                  disabled={readonly}
                >
                  <DeleteIcon />
                </Button>
              )}
            </AddGroup>
          </GroupHeader>
          <ChildrenContainer>{children}</ChildrenContainer>
        </GroupContent>
      </GroupContainer>
    );
  },
);
ConditionGroup.displayName = "ConditionGroup";
export default ConditionGroup;
